import { useCallback, useEffect, useState } from "react";
import { updateBrandStatus } from "../../../apiService/loyalty.service";
import { getProgramId } from "../utils";

export const useLoyaltyOffers = (initialBrands = []) => {
  const [brands, setBrands] = useState(Array.isArray(initialBrands) ? initialBrands : []);
  const [offers, setOffers] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [selectedBrandId, setSelectedBrandId] = useState(
    Array.isArray(initialBrands) && initialBrands.length ? getProgramId(initialBrands[0]) : null
  );
  const [selectedOfferId, setSelectedOfferId] = useState(null);
  const [isLoadingBrands, setIsLoadingBrands] = useState(false);
  const [isLoadingOffers, setIsLoadingOffers] = useState(false);
  const [isLoadingCoupons, setIsLoadingCoupons] = useState(false);
  const [isSavingOrder, setIsSavingOrder] = useState(false);
  const [actionError, setActionError] = useState("");
  const [togglingIds, setTogglingIds] = useState([]);

  useEffect(() => {
    if (!selectedBrandId && brands.length) {
      setSelectedBrandId(getProgramId(brands[0]));
    }
  }, [brands, selectedBrandId]);

  const setBrandList = useCallback((nextList) => {
    setBrands(Array.isArray(nextList) ? nextList : []);
    if (!selectedBrandId && Array.isArray(nextList) && nextList.length) {
      setSelectedBrandId(getProgramId(nextList[0]));
    }
  }, [selectedBrandId]);

  const fetchBrands = useCallback(async (nextBrands = initialBrands) => {
    setIsLoadingBrands(true);
    setActionError("");

    try {
      const safeBrands = Array.isArray(nextBrands) ? nextBrands : [];
      setBrands(safeBrands);
      setSelectedBrandId((prevSelectedBrandId) => {
        if (prevSelectedBrandId || !safeBrands.length) return prevSelectedBrandId;
        return getProgramId(safeBrands[0]);
      });
    } catch (error) {
      setActionError(error?.message || "Failed to load brands.");
    } finally {
      setIsLoadingBrands(false);
    }
  }, [initialBrands]);

  const fetchOffersByBrand = useCallback(async (brandId, nextOffers = []) => {
    if (!brandId) {
      setOffers([]);
      setSelectedOfferId(null);
      setCoupons([]);
      return;
    }

    setIsLoadingOffers(true);
    setActionError("");

    try {
      const safeOffers = Array.isArray(nextOffers) ? nextOffers : [];
      setOffers(safeOffers);
      const firstOfferId = safeOffers.length ? getProgramId(safeOffers[0]) : null;
      setSelectedOfferId(firstOfferId);

      if (firstOfferId) {
        setCoupons([]);
      }
    } catch (error) {
      setActionError(error?.message || "Failed to load offers.");
    } finally {
      setIsLoadingOffers(false);
    }
  }, []);

  const fetchCouponsByOffer = useCallback(async (offerId, nextCoupons = []) => {
    if (!offerId) {
      setCoupons([]);
      return;
    }

    setIsLoadingCoupons(true);
    setActionError("");

    try {
      setCoupons(Array.isArray(nextCoupons) ? nextCoupons : []);
    } catch (error) {
      setActionError(error?.message || "Failed to load coupons.");
    } finally {
      setIsLoadingCoupons(false);
    }
  }, []);

  const handleSelectBrand = useCallback((brand) => {
    const brandId = getProgramId(brand);
    setSelectedBrandId(brandId);
    setSelectedOfferId(null);
    setCoupons([]);
  }, []);

  const handleSelectOffer = useCallback((offer) => {
    const offerId = getProgramId(offer);
    setSelectedOfferId(offerId);
  }, []);

  const handleToggleStatus = useCallback(async (brand) => {
    const brandId = getProgramId(brand);
    if (!brandId) return;

    const nextStatus = brand?.status === "active" ? "inactive" : "active";

    setTogglingIds((prev) => {
      if (prev.includes(brandId)) return prev;
      return [...prev, brandId];
    });

    setBrands((prev) =>
      prev.map((item) =>
        getProgramId(item) === brandId
          ? { ...item, status: nextStatus }
          : item
      )
    );

    try {
      await updateBrandStatus(brandId);
    } catch (error) {
      setBrands((prev) =>
        prev.map((item) =>
          getProgramId(item) === brandId
            ? { ...item, status: brand?.status || item.status }
            : item
        )
      );
      setActionError(error?.message || "Failed to update brand status.");
    } finally {
      setTogglingIds((prev) => prev.filter((id) => id !== brandId));
    }
  }, []);

  const handleToggleFeature = useCallback((brand) => {
    const brandId = getProgramId(brand);
    if (!brandId) return;

    setTogglingIds((prev) => {
      if (prev.includes(brandId)) return prev;
      return [...prev, brandId];
    });

    setBrands((prev) =>
      prev.map((item) =>
        getProgramId(item) === brandId
          ? { ...item, featured: !item.featured }
          : item
      )
    );

    setTogglingIds((prev) => prev.filter((id) => id !== brandId));
  }, []);

  const updateOfferFeatured = useCallback(async (offer) => {
    const offerId = getProgramId(offer);
    if (!offerId) return;

    setActionError("");
    setOffers((prev) =>
      prev.map((item) =>
        getProgramId(item) === offerId ? { ...item, featured: !item.featured } : item
      )
    );
  }, []);

  const saveCoupon = useCallback(async (couponPayload) => {
    setActionError("");

    if (!couponPayload) {
      setActionError("Coupon payload is missing.");
      return null;
    }

    const newCoupon = {
      ...couponPayload,
      id: couponPayload.id ?? Date.now(),
    };

    setCoupons((prev) => {
      const exists = prev.some((item) => getProgramId(item) === getProgramId(newCoupon));
      if (exists) {
        return prev.map((item) =>
          getProgramId(item) === getProgramId(newCoupon) ? { ...item, ...newCoupon } : item
        );
      }
      return [...prev, newCoupon];
    });

    return newCoupon;
  }, []);

  const updateOffer = useCallback(async (offerPayload) => {
    setActionError("");

    if (!offerPayload) {
      setActionError("Offer payload is missing.");
      return null;
    }

    const updatedOffer = {
      ...offerPayload,
      id: offerPayload.id ?? getProgramId(offerPayload),
    };

    setOffers((prev) =>
      prev.map((item) =>
        getProgramId(item) === getProgramId(updatedOffer)
          ? { ...item, ...updatedOffer }
          : item
      )
    );

    return updatedOffer;
  }, []);

  const saveProgramOrder = useCallback(async () => {
    setIsSavingOrder(true);
    setActionError("");

    try {
      // Hook-level reorder logic is intentionally kept generic so the screen can decide
      // whether it is saving brands, offers, or coupons in the future.
    } finally {
      setIsSavingOrder(false);
    }
  }, []);

  return {
    brands,
    offers,
    coupons,
    selectedBrandId,
    selectedOfferId,
    isLoadingBrands,
    isLoadingOffers,
    isLoadingCoupons,
    isSavingOrder,
    actionError,
    togglingIds,
    setBrandList,
    fetchBrands,
    fetchOffersByBrand,
    fetchCouponsByOffer,
    handleSelectBrand,
    handleSelectOffer,
    handleToggleStatus,
    handleToggleFeature,
    updateOfferFeatured,
    saveCoupon,
    updateOffer,
    saveProgramOrder,
    setOffers,
    setCoupons,
  };
};

export default useLoyaltyOffers;
